import React, { FunctionComponent, SVGProps } from "react";
import ChevronDown from "@/assets/icons/chevrons/chevron_down.svg?react";
import {
  Select as AriaSelect,
  SelectProps as AriaSelectProps,
  Button,
  ListBox,
  ListBoxItemProps,
  SelectValue,
  ValidationResult,
  Virtualizer,
  ListLayout,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { Description, FieldError, Label } from "./Field";
import { DropdownItem, DropdownSection, DropdownSectionProps } from "./ListBox";
import { Popover } from "../Popover";
import { cn, composeTailwindRenderProps, focusRing } from "@/utils/helpers";
import CheckIcon from "@/assets/icons/hifi/check.svg?react";
import ArrowDownIcon from "@/assets/icons/arrows/arrow_down.svg?react";

const styles = tv({
  extend: focusRing,
  base: "flex items-center text-start gap-[10px] w-full font-sans font-medium! border border-co-line2 cursor-default rounded-none py-[10px] px-[14px] h-[42px] min-w-[180px] transition-colors duration-200 [-webkit-tap-highlight-color:transparent]",
  variants: {
    variant: {
      default: "hover:border-co-accent",
      transparent: "border-none h-max min-w-auto h-[28px] gap-[5px] p-0",
      ghost:
        "text-co-muted text-caption-1! bg-transparent! border-none! h-max min-w-auto p-0 ps-[8px] gap-[5px]",
      dashboard:
        "h-9 min-w-0 rounded-none border border-co-line bg-co-surface px-3 text-[13px] text-co-text2 hover:border-co-line2 focus-visible:border-co-accent",
      field:
        "min-h-[42px] min-w-0 rounded-none border border-co-line bg-co-surface px-3.5 py-2.5 text-[14px] text-co-text hover:border-co-line2 focus-visible:border-co-accent",
    },
    isDisabled: {
      false: "",
      true: "forced-colors:text-[GrayText] cursor-not-allowed",
    },
    defaultVariants: {
      variant: "default",
    },
  },
});

export type SelectVariant =
  | "default"
  | "transparent"
  | "ghost"
  | "dashboard"
  | "field";

export interface SelectProps<T extends object> extends Omit<
  AriaSelectProps<T>,
  "children"
> {
  label?: string;
  labelClassName?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  variant?: SelectVariant;
  layoutOptions?: { rowHeight: number; gap?: number; padding?: number };
  prioritizeTextValue?: boolean;
  hideChevron?: boolean;
  buttonClassName?: string;
  valueClassName?: string;
  popoverClassName?: string;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  chevronClassName?: string;
  alwaysShowChevron?: boolean;
}

export function Select<T extends object>({
  label,
  labelClassName,
  description,
  errorMessage,
  children,
  items,
  variant = "default",
  layoutOptions,
  prioritizeTextValue,
  hideChevron,
  buttonClassName,
  valueClassName,
  popoverClassName,
  icon: Icon,
  iconClassName,
  chevronClassName,
  alwaysShowChevron = false,
  ...props
}: SelectProps<T>) {
  const showChevron = alwaysShowChevron || !hideChevron;
  const hasSelection = Boolean(props.value ?? props.selectedKey);
  const ariaLabel =
    props["aria-label"] || label || props.placeholder || "Select";

  return (
    <AriaSelect
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col gap-[8px] relative font-sans",
      )}
      aria-label={ariaLabel}
    >
      {label && <Label className={labelClassName}>{label}</Label>}
      <Button
        className={styles({
          isDisabled: props.isDisabled,
          variant,
          className: cn((variant === "field" || variant === "dashboard") && "co-dropdown-trigger", buttonClassName),
        })}
      >
        {Icon && (
          <Icon
            aria-hidden
            className={cn(
              "size-[24px] transition-colors duration-200 shrink-0",
              iconClassName ?? "text-co-faint group-focus-within:text-co-muted",
            )}
          />
        )}
        <SelectValue
          className={cn(
            "flex-1 text-field! truncate",
            hasSelection
              ? ""
              : "text-co-faint",
            valueClassName,
          )}
        >
          {({ defaultChildren, selectedText }) =>
            prioritizeTextValue ? selectedText : defaultChildren
          }
        </SelectValue>
        {(alwaysShowChevron ||
          ((variant === "dashboard" || variant === "field") &&
            showChevron)) && (
            <ChevronDown
              aria-hidden
              className={cn(
                "size-[24px] fill-co-faint forced-colors:text-[ButtonText] group-disabled:fill-co-line2 forced-colors:group-disabled:text-[GrayText]",
                chevronClassName,
              )}
            />
          )}
        {!alwaysShowChevron && variant === "ghost" && !hideChevron && (
          <ArrowDownIcon
            aria-hidden
            className={cn(
              "size-[12px] fill-co-faint forced-colors:text-[ButtonText] group-disabled:fill-co-line2 forced-colors:group-disabled:text-[GrayText]",
              chevronClassName,
            )}
          />
        )}
      </Button>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <Popover className={cn("min-w-(--trigger-width)", popoverClassName)}>
        {layoutOptions ? (
          <Virtualizer layout={ListLayout} layoutOptions={layoutOptions}>
            <ListBox
              items={items}
              className="outline-hidden box-border max-h-[inherit] overflow-auto [clip-path:inset(0_0_0_0)]"
              style={{ display: "block", padding: 0 }}
            >
              {children}
            </ListBox>
          </Virtualizer>
        ) : (
          <ListBox
            items={items}
            className="outline-hidden box-border p-[8px] max-h-[inherit] overflow-auto [clip-path:inset(0_0_0_0)]"
          >
            {children}
          </ListBox>
        )}
      </Popover>
    </AriaSelect>
  );
}

export function SelectItem(props: ListBoxItemProps) {
  return (
    <DropdownItem
      {...props}
      className={cn(props.className, "flex items-center w-full")}
    >
      {({ isSelected }) => (
        <>
          {props.children}
          {isSelected && (
            <CheckIcon className="size-[20px] fill-co-accent ms-auto" />
          )}
        </>
      )}
    </DropdownItem>
  );
}

export function SelectSection<T extends object>(
  props: DropdownSectionProps<T>,
) {
  return <DropdownSection {...props} />;
}
